import { Link } from "react-router-dom";
import { Linkedin, Twitter } from "lucide-react";
import { useT } from "@/i18n/LanguageProvider";
import { SOCIAL } from "@/siteConfig";
import { VEILLE_COMMERCIALE } from "@/config/siteFlags";

const LOGO = "/logo-horsindex.png";

const NAV_LINKS = [
  { to: "/comprendre", id: "comprendre", labelKey: "nav.comprendre", testId: "footer-nav-comprendre" },
  { to: "/methode", id: "methode", labelKey: "nav.methode", testId: "footer-nav-methode" },
  { to: "/services", id: "services", labelKey: "nav.services", testId: "footer-nav-services" },
  { to: "/cadre-juridique", id: "droits", labelKey: "nav.droits", testId: "footer-nav-droits" },
  { to: "/desindexation", id: "deref", label: "Déréférencement Google", testId: "footer-nav-deref" },
];

const INFO_LINKS = VEILLE_COMMERCIALE
  ? [
      { to: "/comprendre", id: "comprendre", labelKey: "nav.comprendre", testId: "footer-nav-comprendre" },
      { to: "/simon-marie", id: "simon-marie", label: "À propos de Simon Marie", testId: "footer-simon-marie" },
      { to: "/contact", id: "contact", labelKey: "footer.contact", testId: "footer-contact" },
      { to: "/mentions-legales", id: "mentions-legales", labelKey: "footer.mentions", testId: "footer-mentions-legales" },
      { to: "/politique-confidentialite", id: "politique", labelKey: "footer.politique", testId: "footer-politique" },
    ]
  : [
      { to: "/simon-marie", id: "simon-marie", label: "À propos de Simon Marie", testId: "footer-simon-marie" },
      { to: "/contact", id: "contact", labelKey: "footer.contact", testId: "footer-contact" },
      { to: "/mentions-legales", id: "mentions-legales", labelKey: "footer.mentions", testId: "footer-mentions-legales" },
      { to: "/conditions-generales", id: "conditions-generales", label: "Conditions générales", testId: "footer-conditions-generales" },
      { to: "/politique-confidentialite", id: "politique", labelKey: "footer.politique", testId: "footer-politique" },
    ];

function FooterColumn({ label, labelTestId, links, t }) {
  return (
    <div className="lg:col-span-3">
      <p
        data-testid={labelTestId}
        className="font-mono text-[12px] font-medium tracking-[0.14em] uppercase text-[#4A4E54] mb-6"
      >
        {label}
      </p>
      <ul className="space-y-3.5 text-[15px] leading-relaxed">
        {links.map((l) => (
          <li key={l.id}>
            <Link
              to={l.to}
              data-testid={l.testId}
              className="text-[#33363B] font-medium hover:text-[#B83A34] transition-colors duration-200"
            >
              {l.label || t(l.labelKey)}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SocialLinks() {
  if (!SOCIAL.linkedin && !SOCIAL.twitter) return null;
  return (
    <div className="mt-7 flex items-center gap-3">
      {SOCIAL.linkedin ? (
        <a
          href={SOCIAL.linkedin}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="LinkedIn - HorsIndex"
          data-testid="footer-linkedin"
          className="text-[#9A9EA4] hover:text-[#181A1D] transition-colors"
        >
          <Linkedin size={16} strokeWidth={1.5} />
        </a>
      ) : null}
      {SOCIAL.twitter ? (
        <a
          href={SOCIAL.twitter}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Twitter / X - HorsIndex"
          data-testid="footer-twitter"
          className="text-[#9A9EA4] hover:text-[#181A1D] transition-colors"
        >
          <Twitter size={16} strokeWidth={1.5} />
        </a>
      ) : null}
    </div>
  );
}

function FooterBrand() {
  return (
    <div className="md:col-span-2 lg:col-span-6">
      <div className="flex items-center gap-3 mb-5">
        <div
          className="inline-flex items-center justify-center overflow-hidden h-11 w-11"
          aria-hidden="true"
        >
          <img
            src={LOGO}
            alt="Logo HorsIndex"
            className="h-11 w-11 object-contain"
            style={{ transform: "scale(1.4)", transformOrigin: "center" }}
          />
        </div>
        <span className="font-sans font-semibold text-2xl text-[#181A1D] tracking-tight">
          HorsIndex
        </span>
      </div>
      <p className="text-[15px] text-[#52565C] leading-relaxed max-w-sm">
        {VEILLE_COMMERCIALE
          ? "Présentation et ressources consacrées à l'exposition numérique."
          : "Réduction de l'exposition numérique professionnelle et patrimoniale."}
      </p>
      {VEILLE_COMMERCIALE ? null : (
        <Link
          to="/contact"
          data-testid="footer-contact-form"
          className="mt-5 inline-block text-[14px] text-[#33363B] hover:text-[#B83A34] transition-colors underline decoration-[#D8D3CD] underline-offset-4 hover:decoration-[#E94B3C]"
        >
          Écrire à HorsIndex via le formulaire
        </Link>
      )}
      <p className="mt-2.5 text-[13px] text-[#7C8086] leading-relaxed">
        {VEILLE_COMMERCIALE ? null : (
          <>
            Téléphone professionnel :{" "}
            <a
              href="tel:+33608033203"
              data-testid="footer-phone-link"
              className="text-[#52565C] hover:text-[#181A1D] transition-colors"
            >
              06 08 03 32 03
            </a>
          </>
        )}
      </p>
      <p className="mt-5 text-[13px] text-[#7C8086] leading-relaxed">
        {VEILLE_COMMERCIALE ? null : (
          <>
            Activité développée dans le cadre d'un CAPE avec Optéos.{" "}
            <Link
              to="/mentions-legales"
              data-testid="footer-cape-mentions"
              className="underline decoration-[#D8D3CD] underline-offset-4 hover:text-[#33363B] hover:decoration-[#33363B] transition-colors"
            >
              Mentions légales
            </Link>
          </>
        )}
      </p>
      <SocialLinks />
    </div>
  );
}

export default function Footer() {
  const { t } = useT();

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#F2F0ED] border-t border-[#E2DEDA] text-[#666A70]"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 lg:pt-20 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-16">
          <FooterBrand />
          {VEILLE_COMMERCIALE ? null : (
            <FooterColumn
              label="Navigation"
              labelTestId="footer-col-nav-label"
              links={NAV_LINKS}
              t={t}
            />
          )}
          <FooterColumn
            label="Informations"
            labelTestId="footer-col-info-label"
            links={INFO_LINKS}
            t={t}
          />
        </div>

        <div
          data-testid="footer-bottom"
          className="mt-12 pt-6 border-t border-[#E2DEDA] flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-[13px] text-[#7C8086] leading-relaxed"
        >
          <p data-testid="footer-copyright">
            © {new Date().getFullYear()} HorsIndex. Tous droits réservés.
          </p>
        </div>
      </div>
    </footer>
  );
}
